import { useState, useRef, useEffect } from "react"
import { Chevron, ExclamationCircleOutline } from '@/components/Icons'

const MultiSelectDropdown = ({
    label,
    id,
    options = [],
    selectedValues = [],
    onChange,
    placeholder = "Select",
    error,
    required = false,
    optional = false,
    disabled = false,
    className = ''
}) => {

    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef(null)

    const baseInputClasses =
        'col-start-1 row-start-1 text-default-900 block w-full rounded-lg bg-default-50 text-base shadow-sm outline outline-1 -outline-offset-1 placeholder:text-base placeholder:font-semibold focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm'

    const selectClasses = error
        ? `${baseInputClasses} cursor-pointer appearance-none p-4 py-[18px] outline-[--error] focus:outline-[--error]`
        : selectedValues.length === 0
            ? `${baseInputClasses} cursor-pointer appearance-none p-4 py-[18px] outline-default-200 text-default-400 focus:outline-default-500`
            : `${baseInputClasses} cursor-pointer appearance-none p-4 py-[18px] outline-default-200 text-default-700 font-semibold focus:outline-default-500`

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleToggle = (value) => {
        if (disabled) return

        const newSelected = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value]

        onChange(newSelected)
    }

    const handleClear = (e) => {
        e.stopPropagation()
        if (!disabled) onChange([])
    }

    const displayText =
        selectedValues.length === 0
            ? placeholder
            : selectedValues.length === 1
                ? selectedValues[0]
                : `${selectedValues.length} selected`

    const checkboxStyles = `
                            .custom-checkbox{
                              appearance:none;
                              width:16px;
                              height:16px;
                              border-radius:4px;
                              background-color:#f8fafc;
                              outline:1px solid #94a3b8;
                              outline-offset:-1px;
                              cursor:pointer;
                              display:inline-block;
                              vertical-align:middle;
                            }
                            
                            .custom-checkbox:checked{
                              background-color:#e2e8f0;
                              outline-color:#1e293b;
                              background-position:center;
                              background-repeat:no-repeat;
                              background-size:12px 12px;
                              background-image:url("data:image/svg+xml;charset=UTF-8,%3csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M9.17569 3.17578L4.8 7.55147L2.82422 5.57569' stroke='%232563eb' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
                            }
                            `

    return (
        <>
            <style>{checkboxStyles}</style>
            <div className={`relative w-full ${className}`} ref={dropdownRef}>

                {/* Label */}
                {label && (
                    <label
                        htmlFor={id}
                        className='block text-sm font-extrabold text-default-500 mb-2'
                    >
                        {label}
                        {required && ' *'}
                        {optional && (
                            <span className='text-2xs font-normal ml-1'>
                                (optional)
                            </span>
                        )}
                    </label>
                )}

                {/* Select Button */}
                <div className="grid grid-cols-1 relative">

                    <button
                        type="button"
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        className={`${selectClasses} text-left pr-12`}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={error ? `${id}-error` : undefined}
                        disabled={disabled}
                    >
                        {displayText}
                    </button>

                    {/* Clear Icon */}
                    {selectedValues.length > 0 && !disabled && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="absolute right-12 top-1/2 -translate-y-1/2 text-default-500 hover:text-default-700 text-lg"
                        >
                            ×
                        </button>
                    )}
                    <Chevron className='pointer-events-none box-content col-start-1 row-start-1 stroke-2 self-center justify-self-end size-6 relative -translate-x-4' />

                </div>


                {isOpen && !disabled && (
                    <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg outline outline-1 outline-default-200 max-h-72 overflow-auto">

                        {options.map((option) => {
                            const isObjectOption = typeof option === 'object' && option !== null
                            const optionValue = isObjectOption ? option.value : option
                            const optionLabel = isObjectOption ? option.label : option

                            return (
                                <label
                                    key={optionValue}
                                    className="flex items-center gap-3 px-4 py-3 cursor-pointer text-base text-default-900 hover:bg-default-100"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(optionValue)}
                                        onChange={() => handleToggle(optionValue)}
                                        className="custom-checkbox shrink-0"
                                    />
                                    <span className="-mt-[2px]">{optionLabel}</span>
                                </label>
                            )
                        })}
                    </div>
                )}
                {error && (
                    <p
                        id={`${id}-error`}
                        className='text-[--error] font-extrabold text-xs mt-2'
                    >
                        <ExclamationCircleOutline className='inline-block mr-1' />
                        {error}
                    </p>
                )}
            </div>
        </>
    )
}

export default MultiSelectDropdown
